import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Mic, Users, GlassWater, Sparkles, Calendar, Handshake, LucideIcon } from 'lucide-react';
import { MenuItemConfig } from '@/components/navbar/menuConfig';

// Configuration visuelle par slug - fallback par défaut pour nouvelles catégories
interface VisualConfig {
  icon: LucideIcon;
  accentColor: string;
  bgGradient: string;
  fallbackTagline: string;
}

const getVisualConfig = (slug: string): VisualConfig => {
  const configs: Record<string, VisualConfig> = {
    'evenement-createur-digital': {
      icon: Sparkles,
      accentColor: '#8B5CF6',
      bgGradient: 'from-[#8B5CF6]/5 to-transparent',
      fallbackTagline: 'Transformez votre audience en communauté engagée'
    },
    'seminaire-team-building-prestige': {
      icon: Users,
      accentColor: 'hsl(var(--primary))',
      bgGradient: 'from-[#1E3A5F]/5 to-transparent',
      fallbackTagline: 'Investissez dans votre capital humain'
    },
    'conference-evenement-client': {
      icon: Mic,
      accentColor: '#C9A227',
      bgGradient: 'from-[#C9A227]/5 to-transparent',
      fallbackTagline: 'Créez l\'évènement qui marque les esprits'
    },
    'soiree-entreprise-paris': {
      icon: GlassWater,
      accentColor: '#E8A4A4',
      bgGradient: 'from-[#E8A4A4]/5 to-transparent',
      fallbackTagline: 'Célébrez vos succès avec élégance'
    }
  };

  return configs[slug] || {
    icon: Calendar,
    accentColor: 'hsl(var(--primary))',
    bgGradient: 'from-[#1E3A5F]/5 to-transparent',
    fallbackTagline: 'Évènement professionnel sur mesure'
  };
};

export interface ProfessionalMenuItem extends MenuItemConfig {
  id: string;
}

export const useProfessionalMenuItems = () => {
  return useQuery({
    queryKey: ['professional-menu-items'],
    queryFn: async (): Promise<ProfessionalMenuItem[]> => {
      // Récupérer TOUTES les catégories professionnelles actives
      const { data, error } = await supabase
        .from('event_categories')
        .select('id, title, slug, description')
        .eq('type_categorie', 'professionnel')
        .eq('is_active', true)
        .order('order');

      if (error) {
        console.error('Error fetching professional menu items:', error);
        throw error;
      }

      // Mapper les données DB vers la structure MenuItemConfig
      const items = (data || []).map((category) => {
        const visualConfig = getVisualConfig(category.slug || '');

        return {
          id: category.id,
          title: category.title,
          path: `/professionnels/evenements/${category.slug}`,
          icon: visualConfig.icon,
          tagline: category.description?.split('.')[0] || visualConfig.fallbackTagline,
          accentColor: visualConfig.accentColor,
          bgGradient: visualConfig.bgGradient
        };
      });

      items.push({
        id: 'devenir-partenaire-static',
        title: 'Devenir partenaire',
        path: '/devenir-partenaire',
        icon: Handshake,
        tagline: 'Rejoignez notre réseau de prestataires d\'excellence',
        accentColor: '#C9A227',
        bgGradient: 'from-[#C9A227]/5 to-transparent'
      });

      return items;
    },
    staleTime: 5 * 60 * 1000, // Cache 5 minutes
    gcTime: 30 * 60 * 1000
  });
};
